<template>
    <article class="node-card">
        <header class="node-card-header">
            <button
                type="button"
                class="node-card-toggle"
                :aria-label="'Toggle recorded allocation for ' + node.node_name"
                :aria-expanded="String(expanded)"
                :aria-controls="detailsId"
                @click="expanded = !expanded">
                <span class="node-card-identity">
                    <v-icon color="primary" aria-hidden="true">mdi-server-outline</v-icon>
                    <span class="node-card-identity-text">
                        <strong class="node-card-name">{{ node.node_name }}</strong>
                        <small class="node-card-nodepool" :title="'Nodepool: ' + nodepoolLabel">Nodepool: {{ nodepoolLabel }}</small>
                        <small>{{ identityLine }}</small>
                        <small class="node-card-capacity" :title="hardwareReference">{{ capacitySummary }}</small>
                    </span>
                </span>
                <span class="node-card-badges">
                    <v-chip small outlined>{{ sessions.length }} {{ sessions.length === 1 ? 'session' : 'sessions' }}</v-chip>
                    <v-chip small :color="node.stop_time ? undefined : 'success'" outlined>{{ node.stop_time ? 'Ended lifetime' : 'Open lifetime' }}</v-chip>
                    <v-icon small aria-hidden="true">{{ expanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                </span>
            </button>
            <button
                type="button"
                class="node-card-pool-link"
                :title="(fromPool ? 'Return to nodepool: ' : 'View nodepool: ') + nodepoolLabel"
                @click="$emit('view-nodepool')">
                {{ fromPool ? 'Back to nodepool' : 'View nodepool' }}
            </button>
        </header>
        <div v-if="expanded" :id="detailsId" class="node-card-details">
            <div class="node-card-lifetime">
                Recorded node lifetime: {{ timestamp(node.start_time) }} → {{ node.stop_time ? timestamp(node.stop_time) : 'Open' }}
                <span class="node-card-hardware-reference">{{ hardwareReference }}</span>
                <span v-if="allocation.resources.some(resource => resource.key.startsWith('nvidia.com/'))" class="node-card-gpu-memory-reference">
                    Total GPU memory (VRAM / HBM): {{ allocation.gpuMemoryTotal === null ? 'Not recorded' : format(allocation.gpuMemoryTotal) + ' GB' }}
                </span>
            </div>
            <NodeAllocationTimeline
                :resources="allocation.resources"
                :start="start"
                :end="end"
                :selection="selectedAllocation"
                :pinned-at="pinnedAt"
                :zoom-can-undo="zoomCanUndo"
                :zoom-can-reset="zoomCanReset"
                @clear-selection="selectedSessionId = null"
                @inspect="inspectedAt = $event"
                @pin="$emit('pin', $event)"
                @zoom="$emit('zoom', $event)"
                @undo-zoom="$emit('undo-zoom')"
                @reset-zoom="$emit('reset-zoom')" />
            <v-expansion-panels flat class="node-card-applications">
                <v-expansion-panel>
                    <v-expansion-panel-header>
                        <span>
                            Application sessions
                            <small>Select a row to highlight it above; expand for containers</small>
                        </span>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                        <div class="node-card-session-scroll">
                            <table class="node-card-sessions">
                                <thead>
                                    <tr>
                                        <th>Application</th>
                                        <th>Lifetime (UTC)</th>
                                        <th v-for="metric in allocation.resources" :key="metric.key">
                                            {{ metric.label }}
                                            <small>Reserved / Limit</small>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <template v-for="session in sessions">
                                        <tr
                                            :key="session.session_id"
                                            :class="{
                                                'node-card-session--selected': selectedSessionId === session.session_id,
                                                'node-card-session--active': activeAtCursor(session)
                                            }"
                                            @click="toggleSession(session.session_id)">
                                            <td>
                                                <div class="node-card-session-identity">
                                                    <v-btn
                                                        icon
                                                        small
                                                        :aria-label="'Container breakdown for ' + (session.image_name || 'Application ' + session.aid)"
                                                        :aria-expanded="String(expandedSessionId === session.session_id)"
                                                        :aria-controls="'node-card-breakdown-' + session.session_id"
                                                        @click.stop="toggleDetails(session.session_id)">
                                                        <v-icon small>
                                                            {{ expandedSessionId === session.session_id ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
                                                        </v-icon>
                                                    </v-btn>
                                                    <div>
                                                        <v-btn
                                                            small
                                                            text
                                                            color="primary"
                                                            :aria-pressed="String(selectedSessionId === session.session_id)"
                                                            @click.stop="toggleSession(session.session_id)">
                                                            {{ session.image_name || 'Application ' + session.aid }}
                                                        </v-btn>
                                                        <small>{{ session.start_uid_full_name || 'User not recorded' }} · {{ session.instance_long_id }}</small>
                                                        <small v-if="admin">{{ poolName(session.rpid) }}</small>
                                                        <small class="node-card-session-size">{{ sessionSizeLabel(session) }}</small>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                {{ timestamp(session.start_time) }}
                                                <small>→ {{ session.stop_time ? timestamp(session.stop_time) : 'Running' }}</small>
                                            </td>
                                            <td v-for="metric in allocation.resources" :key="metric.key">
                                                <span v-if="fullyUnrecorded(session, metric)" class="node-card-unrecorded">
                                                    {{ unrecordedText(session, metric) }}
                                                </span>
                                                <template v-else>
                                                    <span class="node-card-request-value">{{ sessionAmountText(session, metric, 'requests') }}</span>
                                                    <small class="node-card-boundary-label">
                                                        {{ summaryFor(session, metric).limits.status === 'exact' ? '→ up to' : 'Limit' }}
                                                    </small>
                                                    <span class="node-card-limit-value">{{ sessionAmountText(session, metric, 'limits') }}</span>
                                                </template>
                                                <small v-for="warning in cellWarnings(session, metric)" :key="warning" class="node-card-warning">
                                                    {{ warning }}
                                                </small>
                                            </td>
                                        </tr>
                                        <tr
                                            v-if="expandedSessionId === session.session_id"
                                            :key="session.session_id + '-breakdown'"
                                            :id="'node-card-breakdown-' + session.session_id"
                                            class="node-card-session-breakdown">
                                            <td :colspan="2 + allocation.resources.length">
                                                <div class="node-card-size-note">
                                                    <strong>{{ sessionSize(session).label }}</strong>
                                                    <small>{{ sessionSize(session).detail }}</small>
                                                </div>
                                                <div class="node-card-containers">
                                                    <section
                                                        v-for="metric in allocation.resources"
                                                        :key="metric.key"
                                                        class="node-card-container-metric"
                                                        :aria-label="metric.label + ' container resources'">
                                                        <h4>
                                                            {{ metric.label }}
                                                            <small v-if="metric.unit">{{ metric.unit }}</small>
                                                        </h4>
                                                        <p v-if="!containers(session, metric).length" class="node-card-no-containers">
                                                            Container resources not recorded for this session.
                                                        </p>
                                                        <table v-else class="node-card-container-table">
                                                            <thead>
                                                                <tr>
                                                                    <th>Container</th>
                                                                    <th>Role</th>
                                                                    <th>Reserved</th>
                                                                    <th>Limit</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                <tr v-for="(container, index) in containers(session, metric)" :key="index">
                                                                    <td>{{ container.name || 'Container name not recorded' }}</td>
                                                                    <td>{{ roleLabel(container.role) }}</td>
                                                                    <td class="node-card-request-value">{{ measurementText(container.requests) }}</td>
                                                                    <td class="node-card-limit-value">{{ measurementText(container.limits) }}</td>
                                                                </tr>
                                                            </tbody>
                                                        </table>
                                                    </section>
                                                </div>
                                            </td>
                                        </tr>
                                    </template>
                                </tbody>
                            </table>
                        </div>
                    </v-expansion-panel-content>
                </v-expansion-panel>
            </v-expansion-panels>
        </div>
    </article>
</template>

<script>
import NodeAllocationTimeline from './NodeAllocationTimeline.vue'
import { formatAllocationTime, formatResourceMeasurement, recordedTime, resourceMeasurementWarning, sessionAllocationSummary } from './nodeAllocationData'
const numberFormat = new Intl.NumberFormat('en', { maximumFractionDigits: 2 })
const approximateStatuses = ['partial', 'uncapped']

export default {
    name: 'NodeAllocationCard',
    components: { NodeAllocationTimeline },
    props: {
        allocation: { type: Object, required: true },
        start: { type: Number, required: true },
        end: { type: Number, required: true },
        admin: { type: Boolean, default: false },
        resourcePools: { type: Array, required: true },
        nodePools: { type: Array, default: () => [] },
        pinnedAt: { type: Number, default: null },
        zoomCanUndo: { type: Boolean, default: false },
        zoomCanReset: { type: Boolean, default: false },
        nodepoolLabel: { type: String, required: true },
        fromPool: { type: Boolean, default: false },
        initiallyExpanded: { type: Boolean, default: false }
    },
    data() {
        return { selectedSessionId: null, inspectedAt: null, expandedSessionId: null, expanded: this.initiallyExpanded }
    },
    computed: {
        node() {
            return this.allocation.node
        },
        sessions() {
            const sessions = this.allocation.sessions
            return sessions.length < 2
                ? sessions
                : [...sessions].sort(
                      (left, right) =>
                          recordedTime(left.start_time) - recordedTime(right.start_time) || String(left.session_id).localeCompare(String(right.session_id))
                  )
        },
        sessionSummaries() {
            const summaries = new Map()
            for (const session of this.sessions) {
                const metrics = new Map()
                for (const resource of this.allocation.resources) metrics.set(resource.key, sessionAllocationSummary(session, resource))
                summaries.set(session, metrics)
            }
            return summaries
        },
        detailsId() {
            const identity = this.node.nhid === null || this.node.nhid === undefined ? this.node.node_name : this.node.nhid
            return `node-card-details-${identity}`
        },
        identityLine() {
            const parts = [this.allocation.clusterName || 'Platform cluster']
            if (this.admin && this.allocation.poolNames.length) parts.push(this.allocation.poolNames.join(', '))
            return parts.join(' · ')
        },
        capacitySummary() {
            const parts = []
            for (const resource of this.allocation.resources) {
                if (resource.key !== 'cpu' && resource.key !== 'memory') continue
                if (resource.capacity === null || resource.capacity === undefined) continue
                parts.push(this.measurementText({ value: resource.capacity, status: 'exact' }, resource.unit))
            }
            return parts.length ? `Allocatable ${parts.join(' · ')}` : 'Allocatable capacity not recorded'
        },
        hardwareReference() {
            const hardware = []
            const allocatable = []
            for (const resource of this.allocation.resources) {
                if (resource.key !== 'cpu' && resource.key !== 'memory') continue
                if (resource.totalCapacity !== null && resource.totalCapacity !== undefined)
                    hardware.push(this.measurementText({ value: resource.totalCapacity, status: 'exact' }, resource.unit))
                if (resource.capacity !== null && resource.capacity !== undefined)
                    allocatable.push(this.measurementText({ value: resource.capacity, status: 'exact' }, resource.unit))
            }
            const hardwareText = hardware.length ? hardware.join(' · ') : 'Not recorded'
            const allocatableText = allocatable.length ? allocatable.join(' · ') : 'Not recorded'
            return `Worker hardware: ${hardwareText} · allocatable: ${allocatableText}`
        },
        selectedSession() {
            return this.sessions.find(session => session.session_id === this.selectedSessionId) || null
        },
        // The shared pin wins over transient hover inspection for row highlighting.
        activeAt() {
            return this.pinnedAt === null || this.pinnedAt === undefined ? this.inspectedAt : this.pinnedAt
        },
        selectedAllocation() {
            const session = this.selectedSession
            if (!session) return null
            const coverage = this.allocation.resources[0]?.points
            const start = Math.max(this.start, recordedTime(session.start_time), recordedTime(this.node.start_time), coverage?.[0]?.at ?? this.start)
            const end = Math.min(
                this.end,
                session.stop_time ? recordedTime(session.stop_time) : Infinity,
                this.node.stop_time ? recordedTime(this.node.stop_time) : Infinity,
                coverage?.[coverage.length - 1]?.at ?? this.end
            )
            if (start >= end) return null
            return {
                label: `${session.image_name || 'Application ' + session.aid} · ${session.session_id}`,
                start,
                end,
                resources: this.allocation.resources.map(resource => {
                    const summary = this.summaryFor(session, resource)
                    const measurements = {
                        requested: summary.requests.value,
                        limit: summary.limits.value,
                        requestStatus: summary.requests.status,
                        limitStatus: summary.limits.status
                    }
                    return {
                        ...resource,
                        ...measurements,
                        points: [
                            { at: start, ...measurements },
                            { at: end, ...measurements }
                        ]
                    }
                })
            }
        }
    },
    methods: {
        timestamp: formatAllocationTime,
        format(value) {
            return numberFormat.format(value)
        },
        measurementText(measurement, unit) {
            return formatResourceMeasurement(measurement, unit)
        },
        summaryFor(session, metric) {
            return this.sessionSummaries.get(session).get(metric.key)
        },
        sessionAmountText(session, metric, kind) {
            return this.measurementText(this.summaryFor(session, metric)[kind], metric.unit)
        },
        cellWarnings(session, metric) {
            const summary = this.summaryFor(session, metric)
            const warnings = []
            for (const kind of ['requests', 'limits']) {
                const measurement = summary[kind]
                if (!approximateStatuses.includes(measurement.status)) continue
                const warning = resourceMeasurementWarning(measurement, kind)
                if (warning && !warnings.includes(warning)) warnings.push(warning)
            }
            return warnings
        },
        fullyUnrecorded(session, metric) {
            const summary = this.summaryFor(session, metric)
            return summary.requests.status === 'unrecorded' && summary.limits.status === 'unrecorded'
        },
        unrecordedText(session, metric) {
            return this.measurementText(this.summaryFor(session, metric).requests, metric.unit)
        },
        containers(session, metric) {
            return this.summaryFor(session, metric).containers
        },
        roleLabel(role) {
            if (role === 'main') return 'Main'
            if (role === 'sidecar') return 'Sidecar'
            return 'Role not recorded'
        },
        recordedNcu(session) {
            if (session.ncu === null || session.ncu === undefined || session.ncu === '') return null
            const ncu = Number(session.ncu)
            return Number.isFinite(ncu) ? ncu : null
        },
        nodePoolForSession(session) {
            if (typeof session.node_pool !== 'string' || !session.node_pool) return null
            const recorded = session.node_pool
            return (
                this.nodePools.find(pool => pool?.short_id === recorded || (typeof pool?.short_id === 'string' && `nv-app${pool.short_id}` === recorded)) ||
                null
            )
        },
        isStandardSession(session) {
            if (session.tcid !== null && session.tcid !== undefined) return false
            if (session.active_resource === 'ncu') return true
            return !session.node_pool || session.node_pool === 'nv-app'
        },
        sessionSizeLabel(session) {
            if (!this.isStandardSession(session)) return session.node_pool ? `Dedicated pool ${session.node_pool}` : 'Selected pool not recorded'
            const ncu = this.recordedNcu(session)
            return ncu === null ? 'Standard · size not recorded' : `Standard · ${numberFormat.format(ncu)} CU`
        },
        sessionSize(session) {
            if (!this.isStandardSession(session)) {
                const pool = this.nodePoolForSession(session)
                const size = []
                const cpu = Number(pool?.limits?.cpu)
                const memory = Number(pool?.limits?.memory)
                if (pool?.limits?.cpu !== null && pool?.limits?.cpu !== undefined && Number.isFinite(cpu)) size.push(`${numberFormat.format(cpu)} vCPU`)
                if (pool?.limits?.memory !== null && pool?.limits?.memory !== undefined && Number.isFinite(memory))
                    size.push(`${numberFormat.format(memory)} GB`)
                return {
                    label: session.node_pool ? `Dedicated pool ${session.node_pool}${pool?.long_id ? ` · ${pool.long_id}` : ''}` : 'Selected pool not recorded',
                    detail: size.length
                        ? `Current pool size: ${size.join(' / ')} — current pool metadata, not the session-time snapshot.`
                        : 'Current pool size not recorded in the loaded node pools; reserved and limit values below are the recorded session amounts.'
                }
            }
            const ncu = this.recordedNcu(session)
            if (ncu === null)
                return {
                    label: 'Standard size not recorded',
                    detail: 'This session record has no recorded CU size; it is not estimated from the current sizing policy.'
                }
            return {
                label: `Standard size: ${numberFormat.format(ncu)} CU`,
                detail: 'Recorded standard compute size for this session; reserved and limit values below are its recorded container amounts.'
            }
        },
        poolName(rpid) {
            if (rpid === null || rpid === undefined) return 'Billing pool not recorded'
            return this.resourcePools.find(pool => Number(pool.rpid) === Number(rpid))?.long_id || `Resource pool ${rpid}`
        },
        toggleSession(id) {
            this.selectedSessionId = this.selectedSessionId === id ? null : id
        },
        toggleDetails(id) {
            this.expandedSessionId = this.expandedSessionId === id ? null : id
        },
        activeAtCursor(session) {
            const points = this.allocation.resources[0]?.points
            return (
                this.activeAt !== null &&
                points?.length &&
                points[0].at <= this.activeAt &&
                this.activeAt < points[points.length - 1].at &&
                recordedTime(session.start_time) <= this.activeAt &&
                (!session.stop_time || this.activeAt < recordedTime(session.stop_time))
            )
        }
    },
    watch: {
        sessions() {
            if (!this.selectedSession) this.selectedSessionId = null
            if (!this.sessions.some(session => session.session_id === this.expandedSessionId)) this.expandedSessionId = null
            this.inspectedAt = null
        },
        // Drill-down targets a single card by flipping initiallyExpanded; the
        // watcher opens the card without forcing already-expanded cards closed.
        initiallyExpanded(value) {
            if (value) this.expanded = true
        }
    }
}
</script>

<style scoped>
.node-card {
    background: white;
    border: 1px solid #e0e7ef;
    border-radius: 8px;
    overflow: hidden;
}
.node-card-header {
    display: flex;
    align-items: flex-start;
    gap: 8px;
}
.node-card-toggle {
    display: flex;
    flex: 1 1 0;
    min-width: 0;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
    padding: 18px 20px 8px;
    text-align: left;
    color: inherit;
}
.node-card-pool-link {
    flex-shrink: 0;
    max-width: 240px;
    overflow: hidden;
    text-overflow: ellipsis;
    margin: 18px 20px 8px 0;
    padding: 6px 12px;
    border: 1px solid #3381ff;
    border-radius: 4px;
    background: transparent;
    color: #2368d5;
    font-size: 11px;
    font-weight: 500;
    line-height: 16px;
    white-space: nowrap;
    cursor: pointer;
}
.node-card-pool-link:hover {
    background: #edf4ff;
}
.node-card-pool-link:focus-visible {
    outline: 2px solid #3381ff;
    outline-offset: 2px;
}
.node-card-toggle:hover {
    background: #f5f9ff;
}
.node-card-identity {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
}
.node-card-identity-text {
    display: flex;
    flex-direction: column;
    min-width: 0;
}
.node-card-name {
    font-size: 15px;
    font-weight: 600;
    overflow-wrap: anywhere;
}
.node-card-identity-text small {
    font-size: 11px;
    color: #64748b;
    margin-top: 3px;
}
.node-card-identity-text .node-card-nodepool {
    color: #243653;
    font-weight: 500;
    overflow-wrap: anywhere;
}
.node-card-identity-text .node-card-capacity {
    color: #475569;
}
.node-card-badges {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}
.node-card-lifetime {
    padding: 0 20px 16px;
    color: #64748b;
    font-size: 10px;
}
.node-card-gpu-memory-reference {
    display: block;
    margin-top: 6px;
    color: #475569;
}
.node-card-hardware-reference {
    display: block;
    margin-top: 6px;
    color: #475569;
}
.node-card-applications small {
    display: block;
    font-size: 10px;
    color: #64748b;
    margin-top: 4px;
}
.node-card-session-scroll {
    overflow-x: auto;
}
.node-card-sessions {
    border-collapse: collapse;
    width: 100%;
    min-width: 550px;
    text-align: left;
    font-size: 11px;
}
.node-card-sessions th {
    font-weight: 500;
    color: #64748b;
    padding: 8px 12px;
}
.node-card-sessions td {
    padding: 12px;
    border-top: 1px solid #e0e7ef;
    white-space: nowrap;
    vertical-align: top;
}
.node-card-sessions td:first-child {
    white-space: normal;
}
.node-card-sessions tbody tr {
    cursor: pointer;
}
.node-card-session-identity {
    display: flex;
    align-items: flex-start;
    gap: 2px;
}
.node-card-session-identity .v-btn--icon {
    margin-top: 2px;
}
.node-card-session--active {
    background: #f4f7fb;
}
.node-card-session--selected {
    background: #edf4ff;
}
.node-card-session--selected td:first-child {
    box-shadow: inset 3px 0 #3381ff;
}
.node-card-session-size {
    color: #475569;
}
.node-card-boundary-label {
    display: inline-block;
    margin: 0 2px;
    color: #64748b;
}
.node-card-warning {
    color: #95621c;
}
.node-card-request-value {
    color: #2368d5;
}
.node-card-limit-value {
    color: #7751b9;
}
.node-card-unrecorded {
    color: #64748b;
}
.node-card-session-breakdown {
    cursor: default;
}
.node-card-session-breakdown > td {
    background: #f8fafc;
    white-space: normal;
}
.node-card-size-note {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 8px 12px;
    margin-bottom: 12px;
    border-left: 3px solid #3381ff;
    background: #edf4ff;
}
.node-card-size-note strong {
    font-size: 12px;
}
.node-card-size-note small {
    color: #475569;
    font-size: 11px;
    line-height: 1.5;
}
.node-card-containers {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 300px), 1fr));
    gap: 12px;
}
.node-card-container-metric {
    min-width: 0;
    padding: 12px;
    border: 1px solid #dce5ef;
    border-radius: 6px;
    background: white;
    overflow-x: auto;
}
.node-card-container-metric h4 {
    margin: 0 0 10px;
    padding-bottom: 8px;
    border-bottom: 1px solid #e0e7ef;
    font-size: 12px;
    font-weight: 600;
}
.node-card-container-metric h4 small {
    display: inline;
    margin-left: 6px;
    font-size: 10px;
    font-weight: 400;
    color: #64748b;
}
.node-card-container-table {
    border-collapse: collapse;
    width: 100%;
    font-variant-numeric: tabular-nums;
}
.node-card-container-table th {
    font-weight: 500;
    color: #64748b;
    font-size: 10px;
    text-align: left;
    padding: 0 8px 8px 0;
    border-bottom: 1px solid #e0e7ef;
}
.node-card-container-table td {
    border: 0;
    border-bottom: 1px solid #edf1f5;
    padding: 8px 8px 8px 0;
    font-size: 11px;
    white-space: normal;
}
.node-card-container-table tbody tr:last-child td {
    border-bottom: 0;
}
.node-card-container-table th:nth-child(n + 3),
.node-card-container-table td:nth-child(n + 3) {
    text-align: right;
    white-space: nowrap;
}
.node-card-container-table tbody tr {
    cursor: default;
}
.node-card-no-containers {
    font-size: 11px;
    color: #64748b;
    margin: 0;
}
</style>
